import { queryClient } from '.';
import { useMutation } from '@tanstack/react-query';
import { api } from 'api';

import {
    UserRewardsDetailsResponse,
    UserRewardsHistoryResponse,
    UserRewardsSummaryResponse,
} from 'types/myRewards';

import { useErrorHandledQuery } from 'hooks/useErrorHandledQuery';
import { getToken } from 'lib/common';

export const useRewardsSummaryQuery = (userId: number) =>
    useErrorHandledQuery(
        ['rewards', userId, 'summary'],
        async (): Promise<UserRewardsSummaryResponse> =>
            (
                await api.get('/rewards/user/summary', {
                    headers: {
                        authorization: getToken(),
                    },
                })
            ).data.response,
        { enabled: !!userId, staleTime: 300000 }
    );

export const useRewardsDetailsQuery = (userId: number) =>
    useErrorHandledQuery(
        ['rewards', userId, 'details'],
        async (): Promise<UserRewardsDetailsResponse> =>
            (
                await api.get('/rewards/user/details', {
                    headers: {
                        authorization: getToken(),
                    },
                })
            ).data.response,
        { enabled: !!userId, staleTime: 300000 }
    );

export const useRewardsHistoryQuery = (userId: number) =>
    useErrorHandledQuery(
        ['rewards', userId, 'history'],
        async (): Promise<UserRewardsHistoryResponse> =>
            (
                await api.get('/rewards/user/history', {
                    headers: {
                        authorization: getToken(),
                    },
                })
            ).data.response,
        { enabled: !!userId, staleTime: 300000 }
    );

export const useClaimPointsMutation = (userId) =>
    useMutation({
        mutationFn: async () => {
            await api.post(
                '/rewards/user/claim-points',
                {},
                {
                    headers: {
                        authorization: getToken(),
                    },
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['rewards', userId],
                exact: false,
                refetchType: 'active',
            });
        },
    });

export const useRedeemPointsMutation = (userId) =>
    useMutation({
        mutationFn: async () => {
            await api.post(
                '/rewards/user/redeem-reward',
                {},
                {
                    headers: {
                        authorization: getToken(),
                    },
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['rewards', userId],
                exact: false,
                refetchType: 'active',
            });
        },
    });

export const useCheckInMutation = (userId: number) =>
    useMutation({
        mutationFn: async () => {
            await api.post(
                '/rewards/user/check-in',
                {},
                {
                    headers: {
                        authorization: getToken(),
                    },
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['rewards', userId],
                exact: false,
                refetchType: 'active',
            });
        },
    });
